import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Printer, X, Mail, Phone, MapPin, Globe, Hash, CalendarDays, Percent, AlertCircle } from "lucide-react";
import type { QuoteData } from "@/services/opportunityService";
import { useAuth } from "@/hooks/useAuth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Logo importado como asset
import logoPrographic from "@/components/utils/img/Logo Prographic.jpg";

interface QuoteViewerProps {
  data: QuoteData;
  onClose: () => void;
  onSendEmail?: () => Promise<{ success: boolean; message: string } | void>;
  isSending?: boolean;
}

export const QuoteViewer: React.FC<QuoteViewerProps> = ({ data, onClose, onSendEmail, isSending = false }) => {
  const { company: authCompany } = useAuth();
  const [imgError, setImgError] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [resultDialog, setResultDialog] = useState<{ isOpen: boolean; type: 'success' | 'error'; message: string }>({ isOpen: false, type: 'success', message: '' });

  useEffect(() => {
    if (!data || !data.quote) {
      onClose();
    }
  }, [data, onClose]);

  // Bloqueo de scroll y estilos de impresión
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const style = document.createElement("style");
    style.id = "print-isolation-styles-quote";
    style.innerHTML = `
      @media print {
        @page {
          margin: 10mm !important;
          size: letter portrait;
        }
        
        body {
          margin: 0 !important;
          padding: 0 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          background: white !important;
        }

        body > *:not([data-quote-viewer="true"]) {
          display: none !important;
        }

        [data-quote-viewer="true"] {
          position: static !important;
          display: block !important;
          background: white !important;
        }

        .no-print { display: none !important; }

        .doc-container {
          box-shadow: none !important;
          border: none !important;
          width: 100% !important;
          max-width: none !important;
          padding: 0 !important;
        }
      }

      /* Barra de scroll neutra */
      .custom-scrollbar-quote::-webkit-scrollbar { width: 5px; }
      .custom-scrollbar-quote::-webkit-scrollbar-track { background: transparent; }
      .custom-scrollbar-quote::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
    `;
    document.head.appendChild(style);

    return () => {
      document.body.style.overflow = originalOverflow;
      const isolationStyle = document.getElementById("print-isolation-styles-quote");
      if (isolationStyle) isolationStyle.remove();
    };
  }, []);

  if (!data || !data.quote) return null;

  const handlePrint = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.print();
  };

  const { quote, company: quoteCompany, client, seller, items, totals, terms } = data;
  
  // Usar company de auth si tiene branding, fallback a company de la data
  const displayCompany = {
    ...quoteCompany,
    logo_url: authCompany?.logo_url || (quoteCompany as any).logo_url,
    primary_color: authCompany?.primary_color || (quoteCompany as any).primary_color || "#3b82f6",
    secondary_color: authCompany?.secondary_color || (quoteCompany as any).secondary_color || "#1e1b4b",
    name: authCompany?.name || quoteCompany?.name || "Empresa",
    address: authCompany?.address || quoteCompany?.address,
    phone: authCompany?.phone || quoteCompany?.phone,
    email: authCompany?.email || quoteCompany?.email,
  };

  const versionLabel = quote.minor_version > 0 ? `v${quote.version_number}.${quote.minor_version}` : `v${quote.version_number}`;

  return createPortal(
    <div
      data-quote-viewer="true"
      className="fixed inset-0 z-[10000] bg-white flex flex-col pointer-events-auto overflow-hidden animate-in fade-in duration-200"
      onPointerDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      {/* Navbar Minimalista Blanca */}
      <div className="flex-shrink-0 bg-white border-b border-slate-100 px-8 py-2 flex items-center justify-between z-50 no-print shadow-sm">
        <div className="flex items-center gap-3">
          <Hash className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-slate-900 font-bold text-[11px]">Vista Previa #{quote.reference}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-1.5 rounded-md font-bold text-[10px] transition-all flex items-center gap-2 border-none cursor-pointer"
          >
            <Printer className="w-3.5 h-3.5" />
            IMPRIMIR
          </button>
          {onSendEmail && (
            <button
              onClick={(e) => { e.stopPropagation(); setIsConfirmOpen(true); }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-md font-bold text-[10px] transition-all flex items-center gap-2 border-none cursor-pointer shadow-sm shadow-indigo-200"
              disabled={isSending}
            >
              <Mail className="w-3.5 h-3.5" />
              {isSending ? "ENVIANDO..." : "ENVIAR AL CLIENTE"}
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="w-8 h-8 hover:bg-slate-50 text-slate-400 rounded-lg flex items-center justify-center transition-all border-none cursor-pointer"
            title="Cerrar"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Area del Documento - Fondo Blanco */}
      <div className="flex-1 overflow-y-auto custom-scrollbar-quote p-4 md:p-8 bg-slate-50/30 print:bg-white print:p-0">
        <div className="max-w-[800px] mx-auto print:max-w-none">
          
          <div className="doc-container w-full bg-white shadow-sm print:shadow-none border border-slate-100 print:border-none flex flex-col p-8 md:p-12 print:p-0">
            
            <style>{`
              .doc-content { font-family: 'Inter', sans-serif; color: #334155; line-height: 1.4; font-size: 9pt; }
              .brand-primary { color: ${displayCompany.primary_color}; }
              .brand-secondary { color: ${displayCompany.secondary_color}; }
              .brand-primary-bg { background-color: ${displayCompany.primary_color}; }
              .brand-secondary-bg { background-color: ${displayCompany.secondary_color}; }
              .brand-secondary-border { border-color: ${displayCompany.secondary_color}; }
              .table-header-compact { background: #f8fafc; border-bottom: 2px solid ${displayCompany.secondary_color}; }
              .table-cell-compact { padding: 6px 10px; border-bottom: 1px solid #f1f5f9; }
            `}</style>

            <div className="doc-content">
              {/* HEADER COMPACTO */}
              <div className="flex justify-between items-start mb-6 pb-6 border-b border-slate-100">
                <div className="flex items-center gap-6">
                  <div className="w-24 h-16 flex items-center justify-center">
                    {(displayCompany.logo_url && !imgError) ? (
                      <img src={displayCompany.logo_url} alt={displayCompany.name} className="max-w-full max-h-full object-contain" onError={() => setImgError(true)} />
                    ) : (
                      <div className="font-black text-lg tracking-tighter brand-secondary">
                        {displayCompany.name.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="h-10 w-px bg-slate-100 no-print" />
                  <div className="text-[7.5pt] text-slate-400 font-medium leading-tight max-w-[180px]">
                    {displayCompany.address}<br/>
                    {displayCompany.phone} · {displayCompany.email}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[7pt] font-black uppercase tracking-widest mb-1 brand-primary">Cotización Comercial</div>
                  <div className="text-2xl font-black tracking-tighter leading-none mb-1 brand-secondary">#{quote.reference}</div>
                  <div className="flex items-center justify-end gap-2 text-slate-400 font-bold text-[7.5pt]">
                    <CalendarDays className="w-3 h-3" />
                    {quote.date}
                  </div>
                </div>
              </div>

              {/* INFO ORGANIZADA EN 3 COLUMNAS */}
              <div className="grid grid-cols-3 gap-6 mb-8 pb-6 border-b border-slate-100">
                <div className="col-span-1">
                  <div className="text-[7pt] font-black text-slate-400 uppercase tracking-widest mb-2">Cliente</div>
                  <div className="text-sm font-black leading-tight mb-1 brand-secondary">{client?.company_name}</div>
                  <div className="text-xs font-bold text-slate-600 mb-2">{client?.contact_name}</div>
                  <div className="text-[7.5pt] text-slate-400 space-y-1">
                    <div className="flex items-center gap-2"><Mail className="w-3 h-3 text-slate-200" /> {client?.contact_email}</div>
                  </div>
                </div>
                <div className="col-span-1 border-x border-slate-50 px-4">
                  <div className="text-[7pt] font-black text-slate-400 uppercase tracking-widest mb-2">Ejecutivo</div>
                  <div className="text-[sm] font-black leading-tight mb-0.5 brand-secondary">{seller?.name}</div>
                  <div className="text-[7pt] text-slate-500 mb-3">{seller?.email}</div>
                  <div className="text-[7pt] font-black text-slate-400 uppercase mb-0.5">Validez</div>
                  <div className="text-[7.5pt] font-black brand-primary">{quote.validity_date}</div>
                </div>
                <div className="col-span-1 text-right">
                  <div className="text-[7pt] font-black text-slate-400 uppercase tracking-widest mb-2">Resumen</div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-[8pt]">
                      <span className="text-slate-400 font-bold">Moneda:</span>
                      <span className="font-black brand-navy uppercase">{data.currency?.code || 'USD'}</span>
                    </div>
                    <div className="flex justify-between text-[8pt]">
                      <span className="text-slate-400 font-bold">Revisión:</span>
                      <span className="font-black brand-navy uppercase">{versionLabel}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* TABLA CON DESCUENTOS INTEGRADOS */}
              <table className="w-full border-collapse mb-8">
                <thead className="table-header-compact">
                  <tr>
                    <th className="table-cell-compact text-left text-[7pt] font-black uppercase tracking-wider brand-secondary">Servicio / Producto</th>
                    <th className="table-cell-compact text-center text-[7pt] font-black uppercase tracking-wider brand-secondary">Cant.</th>
                    <th className="table-cell-compact text-right text-[7pt] font-black uppercase tracking-wider brand-secondary">Precio Unit.</th>
                    <th className="table-cell-compact text-right text-[7pt] font-black uppercase tracking-wider brand-secondary">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items && items.map((item, idx) => {
                    // Verificación robusta de descuento (por precio o por flag)
                    const hasDiscount = (item.original_price > item.unit_price) || item.has_deviation;
                    const discountAmt = item.original_price - item.unit_price;
                    const discountPct = item.original_price > 0 ? Math.round((discountAmt / item.original_price) * 100) : 0;
                    
                    const currencySymbol = totals?.total_formatted?.includes('$') ? '$' : 'Q';

                    return (
                      <tr key={idx}>
                        <td className="table-cell-compact">
                          <div className="font-black text-slate-800 text-[9pt] leading-tight mb-1">{item.description}</div>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {item.applied_rules && item.applied_rules.map((rule: any, rIdx: number) => {
                              const modifier = parseFloat(rule.price_modifier || rule.modifier || "0");
                              if (Math.abs(modifier) < 0.01) return null;
                              
                              return (
                                <span key={rIdx} className="text-[6pt] font-black text-indigo-600 uppercase border border-indigo-100 bg-indigo-50/50 px-1.5 py-0.5 rounded flex items-center gap-1">
                                  {rule.rule_name || rule.name}: {modifier > 0 ? '+' : ''}{rule.price_type === 'percentage' ? `${modifier}%` : `${currencySymbol}${Math.abs(modifier)}`}
                                </span>
                              );
                            })}
                            {hasDiscount && discountPct > 0 && (
                              <span className="text-[6pt] font-black text-orange-600 uppercase bg-orange-50 px-1.5 py-0.5 rounded border border-orange-100">Ahorro: -{discountPct}%</span>
                            )}
                          </div>
                        </td>
                        <td className="table-cell-compact text-center font-bold text-slate-600 text-xs">{item.quantity}</td>
                        <td className="table-cell-compact text-right">
                          <div className="flex flex-col items-end">
                            {hasDiscount && (
                              <span className="text-[7pt] text-slate-300 line-through font-bold">
                                {item.original_price_formatted}
                              </span>
                            )}
                            <span className="font-black text-[9pt] brand-secondary">
                              {item.unit_price_formatted}
                            </span>
                          </div>
                        </td>
                        <td className="table-cell-compact text-right font-black text-[9.5pt] brand-secondary">
                          {item.line_total_formatted}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* TOTALES Y TÉRMINOS */}
              <div className="grid grid-cols-5 gap-10">
                <div className="col-span-3 space-y-5 pt-2">
                  <div>
                    <div className="text-[7pt] font-black text-slate-300 uppercase tracking-widest mb-2 flex items-center gap-2">
                      <div className="h-0.5 w-4 bg-slate-200" />
                      Términos Legales
                    </div>
                    <div className="space-y-1">
                      {terms && terms.map((term, i) => (
                        <div key={i} className="flex gap-2 text-[7pt] text-slate-500 leading-tight">
                          <span className="font-black min-w-[10px] brand-secondary">{i + 1}.</span>
                          <p className="font-medium">{term}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  {quote.notes && quote.notes.replace(/\[DISCOUNT:.*?\]/g, '').trim() && (
                    <div className="text-[7.5pt] text-slate-500 italic bg-slate-50/50 p-2 border-l border-slate-200">
                      <span className="font-black uppercase text-[6pt] not-italic block mb-0.5 brand-secondary">Notas:</span>
                      {quote.notes.replace(/\[DISCOUNT:.*?\]/g, '').trim()}
                    </div>
                  )}
                </div>
                <div className="col-span-2 flex flex-col items-end pt-2">
                  <div className="w-full space-y-1.5 mb-2">
                    {(() => {
                      let prodSub = 0;
                      let extSub = 0;
                      items?.forEach(item => {
                        const base = item.original_price || item.unit_price;
                        prodSub += base * item.quantity;
                        extSub += (item.line_total - (base * item.quantity));
                      });
                      
                      const currencySymbol = totals?.total_formatted?.includes('$') ? '$' : 'Q';
                      const format = (num: number) => `${currencySymbol}${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

                      return (
                        <>
                          <div className="flex justify-between text-[7.5pt] text-slate-400">
                            <span className="font-bold uppercase tracking-tighter opacity-70">Subtotal Productos</span>
                            <span className="font-bold">{format(prodSub)}</span>
                          </div>
                          {Math.abs(extSub) > 0.01 && (
                            <div className="flex justify-between text-[7.5pt] text-indigo-400">
                              <span className="font-bold uppercase tracking-tighter opacity-70">Ajustes y Extras</span>
                              <span className="font-bold">+{format(extSub)}</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    <div className="flex justify-between text-[8pt] text-slate-600 border-t border-slate-100 pt-1 mt-1">
                      <span className="font-black">Monto Bruto</span>
                      <span className="font-black">{totals?.subtotal_formatted}</span>
                    </div>
                    {totals?.tax > 0 && (
                      <div className="flex justify-between text-[7.5pt] text-slate-400">
                        <span className="font-bold">Impuestos</span>
                        <span className="font-bold">{totals?.tax_formatted}</span>
                      </div>
                    )}
                    <div className="h-0.5 bg-slate-900 mt-2" />
                  </div>
                  <div className="bg-slate-50 border border-slate-200 px-4 py-2 rounded-md flex flex-col items-end w-full shadow-sm">
                    <span className="text-[7pt] font-black uppercase tracking-widest opacity-60 brand-secondary">Total Neto</span>
                    <span className="text-xl font-black tracking-tighter brand-secondary">{totals?.total_formatted}</span>
                  </div>
                </div>
              </div>

              {/* FOOTER */}
              <div className="mt-12 text-center text-[6.5pt] text-slate-300 font-bold uppercase tracking-[0.4em] pt-6 border-t border-slate-50">
                {displayCompany.name} · {displayCompany.website || "www.prographic.com"}
              </div>
            </div>

          </div>
        </div>
      </div>

      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent className="z-[10001]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              ¿Confirmar envío de cotización?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se enviará este documento PDF al correo electrónico{" "}
              <span className="font-bold text-slate-900">{client?.contact_email}</span>. 
              Asegúrate de que toda la información y los descuentos sean correctos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault();
                if (onSendEmail) {
                  try {
                    const result = await onSendEmail();
                    if (result) {
                      setResultDialog({ isOpen: true, type: result.success ? 'success' : 'error', message: result.message });
                    }
                  } catch (err: any) {
                    setResultDialog({ isOpen: true, type: 'error', message: err.message || "Ocurrió un error al intentar enviar el correo." });
                  }
                  setIsConfirmOpen(false);
                }
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              disabled={isSending}
            >
              {isSending ? "Enviando..." : "Sí, enviar ahora"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resultDialog.isOpen} onOpenChange={(open) => !open && setResultDialog(prev => ({ ...prev, isOpen: false }))}>
        <AlertDialogContent className="z-[10001]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {resultDialog.type === 'success' ? (
                <div className="bg-green-100 p-2 rounded-full text-green-600"><MapPin className="w-5 h-5 hidden" /><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></div>
              ) : (
                <div className="bg-red-100 p-2 rounded-full text-red-600"><AlertCircle className="w-5 h-5" /></div>
              )}
              {resultDialog.type === 'success' ? 'Envío Exitoso' : 'Error al Enviar'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base mt-2">
              {resultDialog.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setResultDialog(prev => ({ ...prev, isOpen: false }))}
              className={resultDialog.type === 'success' ? "bg-green-600 hover:bg-green-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}
            >
              Entendido
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>,
    document.body
  );
};
